import { useCallback, useContext, useMemo } from "react";
import { Link, useNavigate } from "react-router";

import { Box, Typography, useMediaQuery } from "@mui/material";
import { GridActionsCellItem } from "@mui/x-data-grid";
import { AppContext } from "App";
import TilleyDataGrid from "components/shared/DataGrid/TilleyDataGrid";
import { CustomWidthTooltip } from "mui-toolkit/CustomWidthTooltip";
import DeleteConfirmationButton from "mui-toolkit/DeleteConfirmationButton";
import { formatNumber, formatPrice } from "utility/formatters";

import { CreateCopyIcon, NavigateIcon } from "../../Icons/Icons";
import { BudgetService } from "../BudgetService";
import { BUDGET_STATUS_ACTIVE, BUDGET_STATUS_DRAFT } from "../helpers";

export default function BudgetSummaryDataTable({
  budgetCards,
  onChange,
  isLoading,
  setIsLoadingCopy,
  pageSizes = [5, 10, 25, 50, 100],
  rowHeight = 80,
}) {
  const { globalState } = useContext(AppContext);
  const navigate = useNavigate();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("lg"), { noSsr: true });

  const createCopyBudget = async (currentBudget) => {
    setIsLoadingCopy(true);
    const { actions: budgetActions } = new BudgetService({
      apiConfig: globalState,
      year: currentBudget?.year,
    });

    try {
      await budgetActions.cloneBudget({ budget_id: currentBudget?.id });
      onChange?.();
    } catch (error) {
      console.error("Error cloning budget:", error);
    } finally {
      setIsLoadingCopy(false);
    }
  };

  const toggleStatus = useCallback(
    (currentBudget) => {
      const _newBudget = { ...currentBudget };
      _newBudget.status = currentBudget.status === BUDGET_STATUS_ACTIVE ? BUDGET_STATUS_DRAFT : BUDGET_STATUS_ACTIVE;

      try {
        onChange?.(_newBudget);
      } catch (error) {
        console.error("Error toggling budget status:", error);
      }
    },
    [onChange]
  );

  const columns = useMemo(
    () => [
      {
        field: "name",
        headerName: "Name",
        flex: 1.5,
        renderCell: (params) => (
          <Typography color="primary" component={Link} to={`/budget/${params.row.id}`}>
            {params.value}
          </Typography>
        ),
      },
      {
        field: "revenue_simple",
        headerName: "Net Income",
        flex: 1,
        renderCell: (params) => {
          return formatPrice(params.row.revenue_simple - params.row.production_cost, 0);
        },
      },
      {
        field: "reported_acreage",
        headerName: "Acres",
        flex: 1,
        renderCell: (params) => {
          return formatNumber(params.row.reported_acreage, 0);
        },
      },
      {
        field: "year",
        headerName: "Year",
        flex: 1,
      },
      {
        field: "status",
        headerName: "Status",
        flex: 1,
        display: "flex",
        renderCell: (params) => {
          const isActive = params.row.status === BUDGET_STATUS_ACTIVE;

          return (
            <Box
              onClick={() => {
                if (!isLoading) {
                  toggleStatus(params.row);
                }
              }}
              sx={{
                backgroundColor: isActive ? "primary.main" : "transparent",
                color: isActive ? "secondary.contrastText" : "text.secondary",
                padding: "8px 24px",
                border: "2px solid",
                borderColor: isActive ? "primary.main" : "text.secondary",
                borderRadius: "999px",
                fontWeight: 600,
                cursor: isLoading ? "not-allowed" : "pointer",
                opacity: isLoading ? 0.7 : 1,
                "&:hover": {
                  backgroundColor: isActive ? (isLoading ? "primary.main" : "primary.dark") : "transparent",
                  borderColor: isActive ? (isLoading ? "primary.main" : "primary.dark") : "text.secondary",
                },
              }}
            >
              {isActive ? "Active" : "Draft"}
            </Box>
          );
        },
        cellClassName: "budgetStatus",
      },
      {
        field: "actions",
        headerName: "Actions",
        type: "actions",
        headerAlign: "right",
        align: "right",
        width: 150,
        sortable: false,
        getActions: (params) => {
          return [
            <CustomWidthTooltip title="Create Copy" placement="top" arrow>
              <GridActionsCellItem
                key="create-copy"
                icon={<CreateCopyIcon />}
                label="Delete"
                onClick={() => createCopyBudget(params.row)}
              />
            </CustomWidthTooltip>,
            <CustomWidthTooltip title="Delete" placement="top" arrow>
              <DeleteConfirmationButton item={params.row} onChange={onChange} />
            </CustomWidthTooltip>,
            <GridActionsCellItem
              key="view"
              icon={<NavigateIcon />}
              label="View Details"
              data-testid={`Open Budget ${params.row.name}`}
              onClick={() => navigate(`/budget/${params.row.id}`)}
            />,
          ];
        },
        flex: 0,
      },
    ],
    [navigate, formatNumber, formatPrice, isLoading, toggleStatus]
  );

  return (
    <>
      <TilleyDataGrid
        rows={budgetCards || []}
        columns={columns}
        pageSizeOptions={pageSizes}
        defaultPageSize={isMobile ? 5 : 10}
        gridKey="budget-cards"
        getRowId={(row) => row.id}
        rowHeight={rowHeight}
        isLoading={isLoading}
        sx={{ width: "99%", boxSizing: "border-box" }}
      />
    </>
  );
}
